import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Star, Shield, Clock, MessageCircle, Upload, X, QrCode, Wallet, CreditCard, MapPinned, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import ChatDialog from "@/components/ChatDialog";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function GuestDashboard() {
  const { t, user, loading } = useApp();
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [threads, setThreads] = useState([]);
  const [reviewing, setReviewing] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [reviewPhotos, setReviewPhotos] = useState([]);
  const [chat, setChat] = useState(null);
  const [qrBooking, setQrBooking] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/auth"); return; }
    if (user.role === "owner") { nav("/owner"); return; }
    if (user.role === "admin") { nav("/admin"); return; }
    reload();
    // eslint-disable-next-line
  }, [user, loading, nav]);

  const reload = () => Promise.all([api.get("/bookings/mine"), api.get("/messages/threads")]).then(([b, th]) => {
    setBookings(b.data); setThreads(th.data);
  });

  const uploadPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 3 - reviewPhotos.length;
    for (const file of files.slice(0, remaining)) {
      const fd = new FormData(); fd.append("file", file);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setReviewPhotos((p) => [...p, data.path]);
      } catch { toast.error("Upload failed"); }
    }
    e.target.value = "";
  };

  const submitReview = async () => {
    try {
      await api.post("/reviews", { booking_id: reviewing.booking_id, rating, comment, photos: reviewPhotos });
      toast.success(t("reviewForm.success"));
      setReviewing(null); setComment(""); setRating(5); setReviewPhotos([]);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const statusMap = { pending: t("guest.pending"), initiated: t("guest.pending"), confirmed: t("guest.confirmed"), failed: t("guest.failed") };

  if (!user) return null;
  return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      <div className="overline text-[#E27B58] mb-2">{t("guest.badge")}</div>
      <h1 className="font-display text-4xl font-bold mb-8">{t("guest.title")}</h1>

      {bookings.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-2xl">
          <p className="text-muted-foreground mb-4">{t("guest.noBookings")}</p>
          <Link to="/search"><Button className="rounded-full bg-[#005C7A]">{t("nav.search")}</Button></Link>
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map((b) => {
            const pm = b.payment_method;
            const pmLabel = pm === "card_on_site" ? "Carta in loco" : pm === "cash" ? "Contanti in loco" : "In loco";
            const PmIcon = pm === "card_on_site" ? CreditCard : Wallet;
            const isPaid = b.payment_status === "paid";
            return (
            <div key={b.booking_id} className="bg-white border border-border rounded-2xl p-5 flex flex-wrap items-center gap-4 justify-between" data-testid={`booking-${b.booking_id}`}>
              <div className="flex-1 min-w-[220px]">
                <div className="font-display text-lg font-semibold">{b.pool_title}</div>
                <div className="text-sm text-muted-foreground">
                  {b.hotel_name && b.hotel_name !== b.pool_title ? `${b.hotel_name} · ` : ""}{b.date} · {b.guests} guest(s)
                </div>
                {isPaid && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#005C7A] bg-[#005C7A]/10 px-2 py-1 rounded-full">
                      <PmIcon className="w-3 h-3" /> {pmLabel}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#E27B58] bg-[#E27B58]/10 px-2 py-1 rounded-full">
                      <MapPinned className="w-3 h-3" /> Da pagare in loco
                    </span>
                    {b.checked_in && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-green-700 bg-green-100 px-2 py-1 rounded-full" data-testid={`guest-entered-${b.booking_id}`}>
                        <CheckCircle2 className="w-3 h-3" /> Entrato
                      </span>
                    )}
                  </div>
                )}
                {isPaid && b.host_reviewed && !b.reviewed && <div className="text-xs text-green-700 mt-1 flex items-center gap-1"><Shield className="w-3 h-3" /> {t("guest.hostReviewedYou")}</div>}
                {isPaid && !b.host_reviewed && !b.reviewed && <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Clock className="w-3 h-3" /> {t("guest.waitingHost")}</div>}
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Da pagare</div>
                <div className="font-display text-xl font-bold text-[#005C7A]">€{b.amount?.toFixed(2)}</div>
                <div className={`text-xs mt-1 ${isPaid ? "text-green-700" : "text-muted-foreground"}`}>{statusMap[b.status] || b.status}</div>
              </div>
              <div className="flex gap-2">
                {isPaid && !b.checked_in && (
                  <Button size="sm" onClick={() => setQrBooking(b)} className="rounded-full bg-[#005C7A] hover:bg-[#004A63]" data-testid={`show-qr-${b.booking_id}`}>
                    <QrCode className="w-3.5 h-3.5 mr-1" /> Mostra QR
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setChat({ poolId: b.pool_id, title: b.hotel_name })} className="rounded-full" data-testid={`chat-${b.booking_id}`}>
                  <MessageCircle className="w-3.5 h-3.5" />
                </Button>
                {isPaid && b.host_reviewed && !b.reviewed && (
                  <Button size="sm" onClick={() => { setReviewing(b); setRating(5); setComment(""); setReviewPhotos([]); }} className="rounded-full bg-[#E27B58] hover:bg-[#C56A4A]" data-testid={`review-${b.booking_id}`}>
                    <Star className="w-3.5 h-3.5 mr-1" /> {t("guest.review")}
                  </Button>
                )}
                {b.reviewed && <span className="text-xs text-muted-foreground self-center">{t("guest.reviewed")} ✓</span>}
              </div>
            </div>
          );})}
        </div>
      )}

      {threads.length > 0 && (
        <div className="mt-10">
          <div className="flex items-center gap-2 mb-4"><MessageCircle className="w-5 h-5 text-[#005C7A]" /><h2 className="font-display text-2xl font-bold">{t("chat.threads")}</h2></div>
          <div className="grid md:grid-cols-2 gap-4">
            {threads.map((th) => (
              <button key={th.pool_id} onClick={() => setChat({ poolId: th.pool_id, title: th.hotel_name || th.other_name })} className="bg-white border border-border rounded-2xl p-4 flex items-center gap-3 text-left hover:border-[#005C7A]/40 transition" data-testid={`g-thread-${th.pool_id}`}>
                <div className="w-12 h-12 rounded-full bg-muted overflow-hidden shrink-0">{th.cover ? <img src={fileUrl(th.cover)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#005C7A]/10" />}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{th.pool_title}</div>
                  <div className="text-xs text-muted-foreground truncate">{th.hotel_name}</div>
                  <div className="text-sm text-muted-foreground truncate mt-0.5">{th.last_from_me && "→ "}{th.last_message}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!reviewing} onOpenChange={(o) => !o && setReviewing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">{t("reviewForm.title")}</DialogTitle></DialogHeader>
          <div className="flex gap-1 justify-center py-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} onClick={() => setRating(n)} data-testid={`star-${n}`}>
                <Star className={`w-8 h-8 ${n <= rating ? "fill-[#E27B58] text-[#E27B58]" : "text-muted-foreground/30"}`} />
              </button>
            ))}
          </div>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Your comment..." data-testid="review-comment" />
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider mb-2">{t("reviewForm.photos")}</div>
            <div className="flex gap-2 flex-wrap">
              {reviewPhotos.map((p) => (
                <div key={p} className="relative w-20 h-20 rounded-lg overflow-hidden bg-muted group">
                  <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setReviewPhotos(reviewPhotos.filter((x) => x !== p))} className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition"><X className="w-3 h-3" /></button>
                </div>
              ))}
              {reviewPhotos.length < 3 && (
                <label className="w-20 h-20 rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:border-[#005C7A] transition">
                  <Upload className="w-4 h-4 text-muted-foreground" />
                  <span className="text-[10px] text-muted-foreground mt-1">{t("reviewForm.addPhotos")}</span>
                  <input type="file" accept="image/*" multiple onChange={uploadPhoto} className="hidden" data-testid="review-upload" />
                </label>
              )}
            </div>
          </div>
          <Button onClick={submitReview} className="rounded-full bg-[#005C7A]" data-testid="submit-review">{t("reviewForm.submit")}</Button>
        </DialogContent>
      </Dialog>

      {chat && <ChatDialog open={!!chat} onOpenChange={(o) => !o && setChat(null)} poolId={chat.poolId} title={chat.title} />}

      <Dialog open={!!qrBooking} onOpenChange={(o) => !o && setQrBooking(null)}>
        <DialogContent className="max-w-sm" data-testid="qr-dialog">
          {qrBooking && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-center">Il tuo pass</DialogTitle>
              </DialogHeader>
              <div className="text-center">
                <div className="text-sm text-muted-foreground mb-1">{qrBooking.pool_title}</div>
                <div className="text-xs text-muted-foreground mb-4">{qrBooking.hotel_name} · {qrBooking.date}</div>
                <div className="bg-white border-2 border-dashed border-[#005C7A] rounded-2xl p-4 mb-4">
                  <img
                    src={`${API_BASE}/api/bookings/${qrBooking.booking_id}/qr.png`}
                    alt="QR"
                    className="w-56 h-56 mx-auto"
                    style={{ imageRendering: "pixelated" }}
                    data-testid="qr-image"
                  />
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-3">ID transazione</div>
                  <div className="font-mono text-2xl font-extrabold text-[#005C7A] tracking-widest" data-testid="guest-short-id">{qrBooking.short_id || qrBooking.booking_id}</div>
                </div>
                <div className="bg-[#005C7A]/5 border border-[#005C7A]/20 rounded-xl p-3 text-left space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Da pagare in loco</span>
                    <span className="font-display text-lg font-bold text-[#005C7A]">€{qrBooking.amount?.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Metodo scelto</span>
                    <span className="text-sm font-semibold">
                      {qrBooking.payment_method === "card_on_site" ? "Carta in loco" : "Contanti in loco"}
                    </span>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground mt-3">Mostra questo QR alla reception all'arrivo.</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
